// src/pages/MagDrops.tsx
import MagDropsWelcome from "@/components/magdrops/MagDropsWelcome";
import PackageSelection from "@/components/magdrops/PackageSelection";
import ProjectDetails from "@/components/magdrops/ProjectDetails";
import QuoteResults from "@/components/magdrops/QuickResults";
import { MagDropsSchema } from "@/components/seo/ServiceSchemas";
import { updatePageSEO } from "@/utils/seo";
import { useEffect, useState } from "react";

type MagDropsStep = "welcome" | "packages" | "details" | "quote";

interface Package {
  id: string;
  title: string;
  subtitle: string;
  price: string;
  timeline: string;
  description: string;
  features: string[];
  perfect_for: string;
  badge?: string;
}

interface ProjectDetails {
  businessType: string;
  audience: string;
  pages: string;
  timeline: string;
  printRun: string;
  distribution: string;
}

interface MagDropsState {
  selectedPackage: Package | null;
  projectDetails: ProjectDetails | null;
  currentStep: MagDropsStep;
}

export default function MagDrops() {
  const [state, setState] = useState<MagDropsState>({
    selectedPackage: null,
    projectDetails: null,
    currentStep: "welcome",
  });
  useEffect(() => {
    updatePageSEO("/magdrops");
  }, []);

  const handleGetQuote = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "packages",
    }));
  };

  const handlePackageSelect = (pkg: Package) => {
    setState((prev) => ({
      ...prev,
      selectedPackage: pkg,
      currentStep: "details",
    }));
  };

  const handleProjectDetailsSubmit = (details: ProjectDetails) => {
    setState((prev) => ({
      ...prev,
      projectDetails: details,
      currentStep: "quote",
    }));
  };

  const handleBackToWelcome = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "welcome",
    }));
  };

  const handleBackToPackages = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "packages",
    }));
  };

  const handleReset = () => {
    setState({
      selectedPackage: null,
      projectDetails: null,
      currentStep: "welcome",
    });
  };

  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case "welcome":
        return <MagDropsWelcome onGetQuote={handleGetQuote} />;

      case "packages":
        return (
          <PackageSelection
            onPackageSelect={handlePackageSelect}
            onBack={handleBackToWelcome}
          />
        );

      case "details":
        if (!state.selectedPackage) {
          return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Error: No Package Selected
                </h2>
                <p className="text-slate-600">Please select a package first.</p>
              </div>
            </div>
          );
        }
        return (
          <ProjectDetails
            selectedPackage={state.selectedPackage}
            onProjectDetailsSubmit={handleProjectDetailsSubmit}
            onBack={handleBackToPackages}
          />
        );

      case "quote":
        if (!state.selectedPackage || !state.projectDetails) {
          return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Error: Missing Quote Data
                </h2>
                <p className="text-slate-600">
                  Please complete the quote flow first.
                </p>
              </div>
            </div>
          );
        }
        return (
          <QuoteResults
            selectedPackage={state.selectedPackage}
            projectDetails={state.projectDetails}
            onReset={handleReset}
          />
        );

      default:
        return <div>Step not implemented yet</div>;
    }
  };

  return (
    <>
      <MagDropsSchema />
      {renderCurrentStep()}
    </>
  );
}
